import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Globe2, Lightbulb, Users, TrendingUp, Shield, Award, CheckCircle2 } from 'lucide-react';

const AboutPage = () => {
  const mission = [
    'Enable sustainable transition through integrated systems, data-driven insights, and effective governance frameworks',
    'Bridge the gap between EU policy, international standards, and practical implementation',
    'Deliver evidence-based solutions that create measurable impact for destinations, cities, and organisations',
  ];

  const expertise = [
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Strategic Advisory',
      description: 'Policy-to-implementation frameworks aligned with EU Green Deal and Cohesion Policy',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Technical Excellence',
      description: 'Climate resilience, energy systems, and sustainable tourism expertise',
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Digital Innovation',
      description: 'AI, IoT, and data platforms for sustainability monitoring and decision support',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Stakeholder Engagement',
      description: 'Collaborative approaches with multi-level governance and capacity building',
    },
  ];

  const values = [
    'Evidence-based decision making',
    'Scalability and replicability',
    'Place-based solutions',
    'Multi-level governance',
    'Stakeholder participation',
    'Innovation and excellence',
  ];

  const scope = [
    {
      region: 'Primary Focus',
      areas: ['European Union Member States', 'European Economic Area', 'EU Candidate Countries'],
    },
    {
      region: 'International',
      areas: ['Mediterranean Region', 'Global South', 'International Development Projects'],
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/33719778/pexels-photo-33719778.jpeg"
            alt="Corporate Building"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 whitespace-nowrap">About SUSTAINESIS GLOBAL LTD</h1>
            <p className="text-2xl text-gray-200 leading-relaxed">
              A high-level European consultancy delivering strategic, technical, and digital solutions for sustainable transition
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">Our Mission</h2>
              <div className="space-y-6">
                {mission.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <CheckCircle2 className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <p className="text-lg text-gray-700 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1655579935873-4916c10a0cae?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Environmental Footprint - Sustainability Impact"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-8 -right-8 bg-red-600 text-white p-8 rounded-xl shadow-2xl max-w-xs">
                <Shield className="w-12 h-12 mb-4" />
                <p className="text-lg font-bold">Trusted Partner for Sustainable Development</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positioning Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Positioning</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategic expertise at the intersection of policy, standards, and implementation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-600 rounded-xl flex items-center justify-center text-white mb-6">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1752659985859-c22670d79563?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Diverse Team Collaboration - Discussion"
                className="rounded-2xl shadow-2xl w-1/2 mx-auto md:w-full"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold text-black mb-8">Our Core Values</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {values.map((value, index) => (
                  <div key={index} className="flex items-center space-x-3 p-4 rounded-lg bg-gradient-to-r from-gray-50 to-red-50 border border-red-100">
                    <Award className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <span className="text-gray-800 font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Geographic Scope */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Globe2 className="w-16 h-16 mx-auto mb-6 text-red-400" />
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Geographic Scope</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              European expertise with global reach
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {scope.map((item, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20 transition-all duration-300">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-red-400 mb-6">{item.region}</h3>
                  <ul className="space-y-3">
                    {item.areas.map((area, idx) => (
                      <li key={idx} className="flex items-center space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-red-400 flex-shrink-0" />
                        <span className="text-gray-200">{area}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Keywords Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">Our Focus Areas</h2>
            <p className="text-xl text-gray-600">Strategic competencies aligned with EU priorities</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-5xl mx-auto">
            {[
              'Climate Resilience',
              'Sustainable Tourism',
              'Urban Systems',
              'EU-Funded Projects',
              'Net Zero Strategies',
              'Digital Transformation',
              'Circular Economy',
              'Energy Efficiency',
              'Data Analytics',
              'Stakeholder Engagement',
              'Governance Models',
              'Impact Assessment',
            ].map((keyword, index) => (
              <span
                key={index}
                className="px-6 py-3 bg-gradient-to-r from-gray-50 to-red-50 text-black rounded-full font-semibold border-2 border-red-200 hover:border-red-500 transition-all duration-300 hover:shadow-md"
              >
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-red-600 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Let's Work Together</h2>
          <p className="text-xl mb-10 text-gray-100">
            Partner with us to achieve your sustainability and climate goals
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-black hover:bg-gray-100 text-lg px-10 py-6">
              Contact Us Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
